/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-07 17:58:17
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues, change } from 'redux-form';
import { FormLayout, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { buildOptionTypes } from 'src/helpers/filter-tree-option';
import Collection from './collection';
import Tag from './tag';
import MultiLevelTag from './multi-level-tag';
import Vendor from './vendor';
import ProductType from './product-type';
import Price from './price';
import VariantsPrice from './variants-price';
import PercentSale from './percent-sale';
import OptionType from './option-type';
import ReviewRatings from './review-ratings';
import StockStatus from './stock-status';
import Weight from './weight';
import RangeSlider from './range-slider';
import VariantsRangeSlider from './variants-range-slider';
import MetafieldType from './metafield-type';
import Location from './location';
import ProductCategory from './product-category';
import NoMetafield from 'src/components/views/filter-tree/item/option-modal/general-tab/no-metafield';
import NoRangeSlider from 'src/components/views/filter-tree/item/option-modal/general-tab/no-range-slider';
import { isRequired, hasTag } from 'src/helpers/form-validation-helper';
import {
  FILTER_TYPE,
  FILTER_OPTION_DEFAULT_VALUES,
  ADVANCED_FIELDS
} from 'src/constants/filter-tree';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';

import * as MultiLevelActions from 'actions/filter-tree/multi-level-actions';

const isRequiredValue = isRequired();

type Props = WithI18nProps;

class GeneralTab extends Component<Props, State> {
  state = {
    optionTypes: [],
    customOptionType: {}
  };

  componentDidMount() {
    const { success, options, metafields, customRangeSliders, locations, appLanguage, i18n } =
      this.props;

    if (success) {
      let optionTypes = buildOptionTypes(
        options,
        metafields,
        customRangeSliders,
        locations,
        appLanguage,
        i18n
      );
      optionTypes = this.removeExistingOptionTypes(optionTypes);
      this.setState({ optionTypes });
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const { success, appLanguage, i18n, formFilterOptionValues: filterOptions } = this.props;
    const {
      success: nextSuccess,
      options: nextOptions,
      metafields: nextMetafields,
      customRangeSliders: nextCustomRangeSliders,
      locations: locationsNext,
      formFilterOptionValues: nextFilterOptions,
      updateFilterOption
    } = nextProps;

    // Check when the modal is loaded before API finish
    if (success !== nextSuccess && nextSuccess === true) {
      let optionTypes = buildOptionTypes(
        nextOptions,
        nextMetafields,
        nextCustomRangeSliders,
        locationsNext,
        appLanguage,
        i18n
      );
      optionTypes = this.removeExistingOptionTypes(optionTypes);
      this.setState({ optionTypes });
    }

    // Turn off show search box when display type is range
    if (
      filterOptions?.variantsPriceDisplayType !== nextFilterOptions?.variantsPriceDisplayType &&
      nextFilterOptions?.variantsPriceDisplayType === 'range'
    ) {
      updateFilterOption('showSearchBoxFilterMobile', false);
      updateFilterOption('showSearchBoxFilterPC', false);
    }
  }

  render() {
    const { optionTypes } = this.state;
    let { touch, optionType, multiLevelTagCollection, emailSupport, themeSetupV2, i18n } =
      this.props;
    let range = 0;
    let variantsRange = 0;

    if (optionType) {
      if (_.startsWith(optionType, FILTER_TYPE.OPTION)) {
        optionType = 'opt';
      } else if (_.startsWith(optionType, FILTER_TYPE.RANGE_SLIDER)) {
        range = optionType.substring(12);
        optionType = FILTER_TYPE.RANGE_SLIDER;
      } else if (_.startsWith(optionType, FILTER_TYPE.VARIANTS_RANGE_SLIDER)) {
        variantsRange = optionType.substring(FILTER_TYPE.VARIANTS_RANGE_SLIDER.length);
        optionType = FILTER_TYPE.VARIANTS_RANGE_SLIDER;
      } else if (_.startsWith(optionType, FILTER_TYPE.METAFIELD)) {
        optionType = 'metafield';
      }
    }

    const components = {
      collection: <Collection multiLevelTagCollection={multiLevelTagCollection} />,
      tag: <Tag touch={touch} />,
      multi_level_tag: <MultiLevelTag />,
      vendor: <Vendor />,
      product_type: <ProductType />,
      product_category: <ProductCategory />,
      price: <Price />,
      variants_price: <VariantsPrice />,
      percent_sale: <PercentSale />,
      opt: <OptionType />,
      review_ratings: <ReviewRatings />,
      stock: <StockStatus />,
      weight: <Weight />,
      location: <Location />,
      range_slider: <RangeSlider range={range} themeSetupV2={themeSetupV2} />,
      variants_range_slider: (
        <VariantsRangeSlider range={variantsRange} themeSetupV2={themeSetupV2} />
      ),
      metafield: <MetafieldType />,
      no_metafield: <NoMetafield />,
      no_range_slider: <NoRangeSlider />
    };

    const variantsRangeSliderSupport = _.startsWith(optionType, FILTER_TYPE.VARIANTS_RANGE_SLIDER)
      ? i18n.translate('AdminHTML.FilterTree.FilterTree.variantsRangeSliderSupport')
      : '';

    return (
      <React.Fragment>
        <FormLayout>
          <Field
            name="optionType"
            component={RenderSelect}
            label={i18n.translate('AdminHTML.FilterTree.FilterTree.field1_Label')}
            options={optionTypes}
            helperText={
              (_.startsWith(optionType, FILTER_TYPE.RANGE_SLIDER) ||
                _.startsWith(optionType, FILTER_TYPE.VARIANTS_RANGE_SLIDER)) && (
                <span style={{ marginTop: '5px' }}>
                  {variantsRangeSliderSupport}
                  <br />
                  {i18n.translate('AdminHTML.FilterTree.FilterTree.field1_HelperText', {
                    nt_email: <TextStyle variation="strong">{emailSupport}</TextStyle>
                  })}
                </span>
              )
            }
            onChange={(e, value, oldValue) => this.changeOptionType(value, oldValue)}
          />

          {/* Render warning message for option Location */}
          {optionType === 'location' && this.renderWarningMessageTypeLocation()}

          {optionType !== 'no_metafield' && optionType !== 'no_range_slider' && (
            <Field
              name="optionLabel"
              component={RenderTextField}
              label={i18n.translate('AdminHTML.FilterTree.FilterTree.field2_Label')}
              validate={[isRequiredValue, hasTag]}
            />
          )}
        </FormLayout>
        {components[optionType]}
      </React.Fragment>
    );
  }

  renderWarningMessageTypeLocation = () => {
    const { i18n, emailSupport } = this.props;
    return (
      <div className="locationWarning">
        <span style={{ color: 'red', fontWeight: 700 }}>
          {i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText_ImportantNOTE'
          )}
        </span>{' '}
        {i18n.translate('AdminHTML.FilterTree.Item.optionModal_GeneralTab_Location_Warning', {
          nt_email: <strong>{emailSupport}</strong>
        })}
      </div>
    );
  };

  removeExistingOptionTypes(optionTypes) {
    const { filterOptions, optionType, initialValues } = this.props;

    const { optionType: optionTypeInit } = initialValues;
    if (!_.isEmpty(filterOptions)) {
      filterOptions.map(item => {
        if (item.filterType !== optionType) {
          _.remove(
            optionTypes,
            i => i.value === item.filterType && i.multiple === false && i.value !== optionTypeInit
          );
        }
        return '';
      });
    }
    return optionTypes;
  }

  // Handle change option type
  changeOptionType = (value, oldValue) => {
    const { optionTypes } = this.state;
    const { updateFilterOption, initialValues } = this.props;

    const { label } = _.find(optionTypes, { value });

    updateFilterOption('optionLabel', label);

    // Update custom option if old/new value is custom option
    // Old value - update state
    if (!_.isEmpty(oldValue) && _.startsWith(oldValue, 'opt_')) {
      const { formFilterOptionValues } = this.props;
      const {
        opt_DisplayType = 'list',
        opt_SelectType = 'single',
        opt_ValueType = 'all',
        opt_ManualValues = [],
        opt_ExcludedValues = []
      } = formFilterOptionValues;

      this.setState(({ customOptionType }) => {
        return {
          customOptionType: {
            ...customOptionType,
            [oldValue]: {
              opt_DisplayType,
              opt_SelectType,
              opt_ValueType,
              opt_ManualValues,
              opt_ExcludedValues
            }
          }
        };
      });
    }

    //New value - update data from state
    if (!_.isEmpty(value) && _.startsWith(value, 'opt_')) {
      const { customOptionType } = this.state;
      let optionData = customOptionType[value];
      optionData = _.isEmpty(optionData)
        ? {
            opt_DisplayType: 'list',
            opt_SelectType: 'single',
            opt_ValueType: 'all',
            opt_ManualValues: [],
            opt_ExcludedValues: []
          }
        : optionData;

      _.map(_.keys(optionData), item => {
        updateFilterOption(item, optionData[item]);
      });
    }

    // Reset advanced data
    const { optionType: optionTypeInit } = initialValues;

    const defaultValues =
      !_.isEmpty(optionTypeInit) && value === optionTypeInit
        ? initialValues
        : FILTER_OPTION_DEFAULT_VALUES;

    _.map(ADVANCED_FIELDS, item => {
      updateFilterOption(item, defaultValues[item]);
    });

    // Reset multi-level states
    this.resetMultiLevelStates();
  };

  resetMultiLevelStates = () => {
    const { resetManualValuesMultiLevelCollections, resetManualValuesMultiLevelTags } = this.props;

    resetManualValuesMultiLevelCollections();
    resetManualValuesMultiLevelTags();
  };
}

const mapStateToProps = (state, props) => {
  const formFilterTreeValues = getFormValues('FilterTreeForm')(state);
  const formFilterOptionValues = getFormValues('OptionModalForm')(state);

  // Get enterprise beta release flag
  const adminAppFeatures = _.get(state.oauth.tenantApp, 'adminAppFeatures', {});
  const multiLevelTagCollection = _.get(adminAppFeatures, 'multiLevelTagCollection', false);

  return {
    success: _.get(state.filterData, 'success', false),
    options: _.get(state.filterData.filterData, 'optionsList', []),
    metafields: _.get(state.filterData.filterData, 'metafieldList', []),
    customRangeSliders: _.get(state.filterData.filterData, 'customRangeSliders', []),
    locations: _.get(state.filterData.filterData, 'locations', []),
    filterOptions: _.get(formFilterTreeValues, 'filterOptions', []),
    optionType: _.get(formFilterOptionValues, 'optionType', ''),
    formFilterOptionValues,
    multiLevelTagCollection,
    emailSupport: _.get(state.oauth, 'emailSupport'),
    themeSetupV2: _.get(state.oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
  };
};

const mapDispatchToProps = dispatch => ({
  updateFilterOption: (field, value) => dispatch(change(`OptionModalForm`, field, value)),
  resetManualValuesMultiLevelTags: () =>
    dispatch(MultiLevelActions.updateManualValuesMultiLevelTags([])),
  resetManualValuesMultiLevelCollections: () =>
    dispatch(MultiLevelActions.updateManualValuesMultiLevelCollections([]))
});
export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(GeneralTab));
